import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Cart, EventService, ProcessSelectors, StateUtils, StateWithProcess, UserIdService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, shareReplay, tap, withLatestFrom } from 'rxjs/operators';
import {
  SAVED_CART_CLONE_CART_PROCESS_ID,
  SAVED_CART_LIST_PROCESS_ID,
  SAVED_CART_RESTORE_CART_PROCESS_ID,
  SAVED_CART_SAVE_CART_PROCESS_ID,
  StateWithMultiCart,
} from '../store';
import { CartActions } from '../store/actions';
import { MultiCartService } from './multi-cart.service';

@Injectable({
  providedIn: 'root',
})
export class SavedCartService {
  constructor(
    protected store: Store<StateWithMultiCart | StateWithProcess<void>>,
    protected userIdService: UserIdService,
    protected userAccountFacade: UserAccountFacade,
    protected multiCartService: MultiCartService,
    protected eventService: EventService,
  ) {}

  /**
   * Loads a single saved cart
   */
  loadSavedCart(cartId: string): void {
    this.userIdService.takeUserId(true).subscribe((userId) => {
      return this.store.dispatch(new CartActions.LoadSavedCart({ userId, cartId }));
    });
  }

  /**
   * Gets a single saved cart
   * it won't emit if the delete saved cart event gets triggered to avoid race condition between actions
   *
   * @param cartId
   * @returns observable with cart
   */
  get(cartId: string): Observable<Cart | undefined> {
    return this.getSavedCart(cartId).pipe(
      filter((state) => !!state),
      tap((state) => {
        if (!(state.loading || state.success || state.error)) {
          this.loadSavedCart(cartId);
        }
      }),
      filter((state) => state.success || !!state.error),
      map((state) => state.value),
    );
  }

  /**
   * Gets the selected cart state
   *
   * @param cartId
   * @returns observable of selected cart with loader state
   */
  getSavedCart(cartId: string): Observable<StateUtils.ProcessesLoaderState<Cart>> {
    return this.multiCartService.getCartEntity(cartId);
  }

  /**
   * Returns true when there are no operations on that in progress and it is not currently loading
   *
   * @param cartId
   */
  isStable(cartId: string): Observable<boolean> {
    return this.multiCartService.isStable(cartId);
  }

  /**
   * Loads a list of saved carts
   */
  loadSavedCarts(): void {
    this.userIdService.takeUserId(true).subscribe((userId) => {
      return this.store.dispatch(new CartActions.LoadSavedCarts({ userId }));
    });
  }

  /**
   * Gets a list of saved carts
   *
   * @returns observable with list of saved carts
   */
  getList(): Observable<Cart[]> {
    return this.getSavedCartList().pipe(
      withLatestFrom(this.getSavedCartListProcess()),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      tap(([_, state]) => {
        if (!(state.loading || state.success || state.error)) {
          this.loadSavedCarts();
        }
      }),
      // eslint-disable-next-line deprecation/deprecation
      pluck(0),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Gets a list of saved carts from all carts in the state
   * by filtering through the carts that are not wishlist and not saved cart
   *
   * @returns observable with list of saved carts
   */
  getSavedCartList(): Observable<Cart[]> {
    return combineLatest({ carts: this.multiCartService.getCarts(), user: this.userAccountFacade.get() }).pipe(
      distinctUntilChanged(),
      map(({ carts, user }) =>
        carts.filter((cart) => (user?.customerId !== undefined ? false : true) && cart?.saveTime),
      ),
    );
  }

  /**
   * Gets the loading flag of getting a list of saved carts
   *
   * @returns observable with boolean of the loading state
   */
  getSavedCartListProcessLoading(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessLoadingFactory(SAVED_CART_LIST_PROCESS_ID)),
    );
  }

  /**
   * Gets the loading state of getting a list of saved carts
   *
   * @returns observable with boolean of the loader state
   */
  getSavedCartListProcess(): Observable<StateUtils.LoaderState<any>> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessStateFactory(SAVED_CART_LIST_PROCESS_ID)),
    );
  }

  /**
   * Clears the process state of performing a saved cart
   */
  clearSavedCarts(): void {
    this.store.dispatch(new CartActions.ClearSavedCarts());
  }

  /**
   * Gets the loading state of restoring saved cart
   *
   * @returns observable with boolean of the loading state
   */
  getRestoreSavedCartProcessLoading(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessLoadingFactory(SAVED_CART_RESTORE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the success state of restoring saved cart
   *
   * @returns observable with boolean of the success state
   */
  getRestoreSavedCartProcessSuccess(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessSuccessFactory(SAVED_CART_RESTORE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the error state of restoring saved cart
   *
   * @returns observable with boolean of the error state
   */
  getRestoreSavedCartProcessError(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessErrorFactory(SAVED_CART_RESTORE_CART_PROCESS_ID)),
    );
  }

  /**
   * Clears the process state of performing a restore saved cart
   */
  clearRestoreSavedCart(): void {
    this.store.dispatch(new CartActions.ClearRestoreSavedCart());
  }

  /**
   * Triggers delete saved cart
   * @param cartId
   */
  deleteSavedCart(cartId: string): void {
    this.userIdService.takeUserId(true).subscribe((userId) => {
      return this.multiCartService.deleteCart(cartId, userId);
    });
  }

  /**
   * Gets the loading state of saving a cart
   *
   * @returns observable with boolean of the loading state
   */
  getSaveCartProcessLoading(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessLoadingFactory(SAVED_CART_SAVE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the success state of saving a cart
   *
   * @returns observable with boolean of the success state
   */
  getSaveCartProcessSuccess(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessSuccessFactory(SAVED_CART_SAVE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the error state of saving a cart
   *
   * @returns observable with boolean of the error state
   */
  getSaveCartProcessError(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessErrorFactory(SAVED_CART_SAVE_CART_PROCESS_ID)),
    );
  }

  /**
   * Clears the process state of performing a save cart
   */
  clearSaveCart(): void {
    this.store.dispatch(new CartActions.ClearSaveCart());
  }

  /**
   * Triggers a clone saved cart
   *
   * @param cartId
   */
  cloneSavedCart(cartId: string, saveCartName?: string): void {
    this.userIdService.takeUserId(true).subscribe((userId) => {
      return this.store.dispatch(new CartActions.CloneSavedCart({ userId, cartId, saveCartName }));
    });
  }

  /**
   * Gets the loading state of cloning a saved cart
   *
   * @returns observable with boolean of the loading state
   */
  getCloneSavedCartProcessLoading(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessLoadingFactory(SAVED_CART_CLONE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the success state of cloning a saved cart
   *
   * @returns observable with boolean of the success state
   */
  getCloneSavedCartProcessSuccess(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessSuccessFactory(SAVED_CART_CLONE_CART_PROCESS_ID)),
    );
  }

  /**
   * Gets the error state of cloning a saved cart
   *
   * @returns observable with boolean of the error state
   */
  getCloneSavedCartProcessError(): Observable<boolean> {
    return (<Store<StateWithProcess<void>>>this.store).pipe(
      select(ProcessSelectors.getProcessErrorFactory(SAVED_CART_CLONE_CART_PROCESS_ID)),
    );
  }

  /**
   * Clears the process state of cloning a saved cart
   */
  clearCloneSavedCart(): void {
    this.store.dispatch(new CartActions.ClearCloneSavedCart());
  }
}
